import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { redirectToInternalLink } from "../lib/redirectHelper";
import { useThemeColors } from "../hooks/useThemeColors";

export function BannerCarousel({ banners = [], eventSlug, setSidebarOpen }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();
  const { themeColor } = useThemeColors();

  // Auto-slide functionality
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  // Minimum distance for swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < banners.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleInternalClick = async (banner, e) => {
    e.preventDefault();
    await redirectToInternalLink(
      banner.to_type,
      banner.to_id,
      eventSlug,
      navigate
    );
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-gray-100 overflow-hidden">
      {/* Container with 5:2 aspect ratio */}
      <div
        className="relative w-full"
        style={{ aspectRatio: "5/2" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Banner Images Container with sliding animation */}
        <div className="relative w-full h-full overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out h-full"
            style={{
              transform: `translateX(-${
                currentIndex * (100 / banners.length)
              }%)`,
              width: `${banners.length * 100}%`,
            }}
          >
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className="w-full h-full flex-shrink-0"
                style={{ width: `${100 / banners.length}%` }}
              >
                {banner.link_type === "external" && banner.external_url ? (
                  <a
                    href={banner.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-inset"
                    style={{ "--tw-ring-color": themeColor || "#3B82F6" }}
                    aria-label="Open external link"
                    onClick={() => setSidebarOpen && setSidebarOpen(false)}
                  >
                    {banner.image_url ? (
                      <img
                        src={banner.image_url}
                        alt={banner.name || "Banner"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-gradient-to-r flex items-center justify-center"
                        style={{
                          background: `linear-gradient(to right, ${
                            themeColor || "#3B82F6"
                          }, #9333EA)`,
                        }}
                      >
                        <span className="text-white font-semibold text-sm text-center px-2">
                          {banner.name || "Banner"}
                        </span>
                      </div>
                    )}
                  </a>
                ) : banner.link_type === "internal" &&
                  banner.to_type &&
                  banner.to_id ? (
                  <Link
                    to="#"
                    onClick={(e) => {
                      handleInternalClick(banner, e);
                      setSidebarOpen && setSidebarOpen(false);
                    }}
                    className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-inset"
                    style={{ "--tw-ring-color": themeColor || "#3B82F6" }}
                    aria-label="View details"
                  >
                    {banner.image_url ? (
                      <img
                        src={banner.image_url}
                        alt={banner.name || "Banner"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-gradient-to-r flex items-center justify-center"
                        style={{
                          background: `linear-gradient(to right, ${
                            themeColor || "#3B82F6"
                          }, #9333EA)`,
                        }}
                      >
                        <span className="text-white font-semibold text-sm text-center px-2">
                          {banner.name || "Banner"}
                        </span>
                      </div>
                    )}
                  </Link>
                ) : (
                  <>
                    {banner.image_url ? (
                      <img
                        src={banner.image_url}
                        alt={banner.name || "Banner"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-gradient-to-r flex items-center justify-center"
                        style={{
                          background: `linear-gradient(to right, ${
                            themeColor || "#3B82F6"
                          }, #9333EA)`,
                        }}
                      >
                        <span className="text-white font-semibold text-sm text-center px-2">
                          {banner.name || "Banner"}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
